import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { triageProbelaufQuery, triageRegelnQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenTitel } from './AufgabeKontext'
import { TriageRegelAnlegen } from './TriageRegelAnlegen'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **Regel-Verwaltung** (Phase 6, Mockup `v-eingang` → „Regeln verwalten").
 *
 * ## Warum der Probelauf oben steht und nicht unten
 *
 * Die einzige Frage vor dem Hochstufen einer Regel lautet: *was trifft sie eigentlich?*
 * Eine Trefferzahl beantwortet sie nicht – „12 Treffer" sagt nichts darüber, ob echte
 * Arbeit darunter ist. Deshalb zeigt der Probelauf die **Titel**, bevor irgendwo ein
 * Schalter umgelegt wird. Dieselbe Zweistufigkeit wie beim Rückschreiben in Phase 5, und
 * aus demselben Grund: eine zu breite Regel nimmt jemandem eine Aufgabe aus dem Blickfeld,
 * ohne dass es auffällt.
 *
 * ## Warum es keinen Knopf gibt, der alle scharf stellt
 *
 * Konzept Kap. 4.1: **kein globaler Automatik-Schalter.** Jede Regel wird einzeln
 * hochgestuft, und erst wenn ihre Treffer stimmen. Ein „alle aktivieren" wäre genau dieser
 * Schalter mit einem anderen Namen.
 */
export function TriageRegeln() {
  const queryClient = useQueryClient()
  const { data: regeln = [], error } = useQuery(triageRegelnQuery)
  const [probeOffen, setProbeOffen] = useState(false)
  const [anlegenOffen, setAnlegenOffen] = useState(false)
  const { data: probe } = useQuery({ ...triageProbelaufQuery, enabled: probeOffen })

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['triage'] })

  const stufeSetzen = useMutation({
    mutationFn: (v: { id: string; stufe: 'vorschlag' | 'bestaetigung' | 'automatisch' }) =>
      trpc.triage.stufeSetzen.mutate(v),
    onSuccess: neuLaden,
  })
  const aktivSetzen = useMutation({
    mutationFn: (v: { id: string; aktiv: boolean }) => trpc.triage.aktivSetzen.mutate(v),
    onSuccess: neuLaden,
  })
  /**
   * **Die Rangfolge zum Anfassen** (20.08.2026, [E-153](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Die Reihenfolge entscheidet, welche Regel greifen darf – *die erste gewinnt*. Bis heute
   * war sie nicht änderbar: die Anlegen-Maske schrieb bei jeder Regel fest `100`, und ein
   * Feld dafür gab es nirgends. Fünf Regeln mit demselben Rang später hieß „die erste"
   * praktisch „die, die die Datenbank zuerst zurückgibt".
   */
  const verschieben = useMutation({
    mutationFn: (v: { id: string; richtung: 'hoch' | 'runter' }) =>
      trpc.triage.verschieben.mutate(v),
    onSuccess: neuLaden,
  })
  const anwenden = useMutation({
    mutationFn: () => trpc.triage.jetztAnwenden.mutate(),
    onSuccess: async () => {
      neuLaden()
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  if (error) return null

  const automatische = regeln.filter((r) => r.aktiv && r.stufe === 'automatisch').length

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">Triage-Regeln</h3>
          <span className="text-[11.5px] text-muted">
            Regeln laufen vor der KI – deterministisch, nachvollziehbar und kostenlos
          </span>
        </div>

        {regeln.length === 0 ? (
          <>
            <p className="mt-2 text-[12.5px] text-muted">Keine Regeln hinterlegt.</p>
            <div className="mt-2">
              <Button variant="ghost" onClick={() => setAnlegenOffen((v) => !v)}>
                {anlegenOffen ? 'Abbrechen' : '+ Neue Regel'}
              </Button>
            </div>
            {anlegenOffen && <TriageRegelAnlegen fertig={() => setAnlegenOffen(false)} />}
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[560px] mt-3 w-full border-collapse text-[12px]">
                <thead>
                  <tr>
                    {['Rang', 'Regel', 'prüft', 'Aktion', 'Treffer', 'Stufe', ''].map((h) => (
                      <th
                        key={h}
                        className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {regeln.map((r, i) => {
                    const aktion = r.aktion as { status?: string; poolIds?: string[] } | null
                    return (
                      <tr key={r.id} className={r.aktiv ? '' : 'opacity-50'}>
                        {/**
                         * **Die Position, nicht die gespeicherte Zahl.** `reihenfolge` ist eine
                         * Rechengröße mit Lücken (10, 20, 30 …); sie anzuzeigen lädt dazu ein,
                         * sie für eine Bedeutung zu halten. Was zählt, ist *wer vor wem* kommt.
                         */}
                        <td className="border-b border-line px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <span className="tabular w-4 text-muted">{i + 1}</span>
                            <button
                              type="button"
                              disabled={i === 0 || verschieben.isPending}
                              onClick={() => verschieben.mutate({ id: r.id, richtung: 'hoch' })}
                              title="früher prüfen"
                              aria-label={`${r.name} nach oben`}
                              className="cursor-pointer rounded px-1 leading-none text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-25"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={i === regeln.length - 1 || verschieben.isPending}
                              onClick={() => verschieben.mutate({ id: r.id, richtung: 'runter' })}
                              title="später prüfen"
                              aria-label={`${r.name} nach unten`}
                              className="cursor-pointer rounded px-1 leading-none text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-25"
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                        <td className="border-b border-line px-2 py-1.5">
                          <strong>{r.name}</strong>
                        </td>
                        <td className="border-b border-line px-2 py-1.5 font-mono text-[11px]">
                          {r.feld} {OPERATOR_LABELS[r.operator] ?? r.operator} „{r.wert}"
                        </td>
                        <td className="border-b border-line px-2 py-1.5 text-ink2">
                          {aktion?.status ?? '–'}
                          {aktion?.poolIds?.length ? ` → ${aktion.poolIds.join(', ')}` : ''}
                        </td>
                        <td className="tabular border-b border-line px-2 py-1.5">
                          {/**
                           * **Die Steuergröße** (Konzept Kap. 7). Eine Regel mit null Treffern
                           * ist entweder überflüssig oder falsch geschrieben – und man sieht
                           * es nur hier.
                           */}
                          {r.treffer === 0 ? (
                            <span className="text-muted" title="noch nie gegriffen">
                              0
                            </span>
                          ) : (
                            <span
                              title={
                                r.letzterTreffer
                                  ? `zuletzt ${new Date(r.letzterTreffer).toLocaleString('de-DE')}`
                                  : undefined
                              }
                            >
                              {r.treffer}
                            </span>
                          )}
                        </td>
                        <td className="border-b border-line px-2 py-1.5">
                          <select
                            className="rounded-md border border-line px-1.5 py-0.5 text-[11.5px]"
                            value={r.stufe}
                            onChange={(e) =>
                              stufeSetzen.mutate({
                                id: r.id,
                                stufe: e.target.value as 'vorschlag',
                              })
                            }
                          >
                            <option value="vorschlag">nur vorschlagen</option>
                            <option value="bestaetigung">mit Bestätigung</option>
                            <option value="automatisch">automatisch</option>
                          </select>
                        </td>
                        <td className="border-b border-line px-2 py-1.5">
                          <Button
                            variant="ghost"
                            onClick={() => aktivSetzen.mutate({ id: r.id, aktiv: !r.aktiv })}
                          >
                            {r.aktiv ? 'abschalten' : 'einschalten'}
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button variant="ghost" onClick={() => setAnlegenOffen((v) => !v)}>
                {anlegenOffen ? 'Anlegen abbrechen' : '+ Neue Regel'}
              </Button>
              <Button variant="ghost" onClick={() => setProbeOffen((v) => !v)}>
                {probeOffen ? 'Probelauf ausblenden' : 'Probelauf: was würde greifen?'}
              </Button>
              <Button
                disabled={automatische === 0 || anwenden.isPending}
                onClick={() => anwenden.mutate()}
                title={
                  automatische === 0
                    ? 'Keine Regel steht auf „automatisch" – es gäbe nichts anzuwenden.'
                    : undefined
                }
              >
                {anwenden.isPending
                  ? 'wird angewendet …'
                  : `${automatische} automatische Regel${automatische === 1 ? '' : 'n'} anwenden`}
              </Button>
              {anwenden.data && (
                <span className="text-[12px] text-ink2">
                  {anwenden.data.eingeordnet} von {anwenden.data.geprueft} eingeordnet
                </span>
              )}
            </div>

            {anlegenOffen && <TriageRegelAnlegen fertig={() => setAnlegenOffen(false)} />}

            {probeOffen && (
              <div className="mt-3 rounded-lg border border-line bg-[#fafaf8] p-3">
                {!probe ? (
                  <p className="text-[12px] text-muted">wird geprüft …</p>
                ) : probe.getroffen.length === 0 ? (
                  <p className="text-[12px] text-muted">
                    Keine der {probe.geprueft} offenen Aufgaben wird von einer Regel getroffen.
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-[12px]">
                      <strong>{probe.getroffen.length}</strong> von {probe.geprueft} offenen
                      Aufgaben würden eingeordnet – <em>nichts davon ist geschehen.</em>
                    </p>
                    {/**
                     * **Die Titel, nicht nur die Zahl.** Die Frage vor dem Hochstufen lautet:
                     * steht hier etwas drin, das echte Arbeit ist? Eine Zahl beantwortet sie
                     * nicht.
                     */}
                    <ul className="grid gap-1 text-[12px]">
                      {probe.getroffen.slice(0, 40).map((t) => (
                        <li key={`${t.taskId}-${t.regelName}`} className="flex gap-2">
                          <span className="shrink-0 text-muted">{t.regelName}</span>
                          {/**
                           * Die Frage beim Probelauf lautet „ist das echte Arbeit?" – und
                           * die beantwortet der Titel allein oft nicht.
                           */}
                          <AufgabenTitel
                            aufgabeId={t.taskId}
                            titel={t.titel}
                            className="text-ink2"
                          />
                        </li>
                      ))}
                    </ul>
                    {probe.getroffen.length > 40 && (
                      <p className="mt-1.5 text-[11px] text-muted">
                        … und {probe.getroffen.length - 40} weitere
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        <p className="mt-2 text-[11px] text-muted">
          Es gibt bewusst keinen Schalter, der alle Regeln auf einmal scharf stellt. Jede wird
          einzeln hochgestuft – und erst, wenn ihre Treffer stimmen.
        </p>
      </CardPad>
    </Card>
  )
}

const OPERATOR_LABELS: Record<string, string> = {
  beginnt_mit: 'beginnt mit',
  enthaelt: 'enthält',
  ist_gleich: 'ist gleich',
}
